import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Table, message, Button, Tooltip as AntTooltip } from "antd";
import Swal from "sweetalert2";
import {
  Trash2,
  RotateCcw,
  ChevronUp,
} from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { secureExportToExcel } from "../../utils/secureExcelUtils";
import { fetchListOrderList, deleteListOrder } from "../../core/api/mediaplan";

const ListOrdersList = () => {
  const [orders, setOrders] = useState([]);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const pendingRequestRef = useRef(null);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  
  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const transformData = (data) =>
    data.flatMap((order) =>
      (order.items && order.items.length > 0 ? order.items : [{}]).map((item, itemIndex) => ({
        ...order,
        ...item,
        key: `${order.id}-${item.id || itemIndex}`,
        orderRowSpan: itemIndex === 0 ? (order.items?.length || 1) : 0,
      }))
    );

  const fetchOrderData = useCallback(async (page = 1, pageSize = 5000) => {
    const requestKey = `${selectedClients.join(',')}-${page}-${pageSize}`;
    if (pendingRequestRef.current === requestKey || loading) {
      return;
    }
    pendingRequestRef.current = requestKey;
    
    setLoading(true);
    try {
      const response = await fetchListOrderList(selectedClients, page, pageSize);
      const transformedData = transformData(response.data);
      setOrders(transformedData);
      setTotalItems(response.data.count);
      setTotalPages(response.data.total_pages);
      setPage(response.data.page);
      setPageSize(response.data.page_size);
    } catch (error) {
      console.error("Error fetching list orders:", error);
      message.error("Failed to load list orders. Please try again.");
    } finally {
      if (pendingRequestRef.current === requestKey) {
        pendingRequestRef.current = null;
      }
      setLoading(false);
    }
  }, [selectedClients, loading]);

  useEffect(() => {
    fetchOrderData();
  }, [selectedClients]);

  const handlePageChange = (newPage, newPageSize) => {
    fetchOrderData(newPage, newPageSize);
  };

  const cellRenderer = useCallback(({ value }) => value === null || value === undefined ? "-" : value, []);

  const dateFormatter = ({ value }) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleDateString();
  };

  const priceFormatter = useCallback(({ value }) => {
    if (value === null || value === undefined) return "-";
    return `$${parseFloat(value).toFixed(2)}`;
  }, []);

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        columnDefs: [
          { headerName: "List", field: "list_name", cellRenderer, flex: 1 },
          { headerName: "Segment", field: "segment_name", cellRenderer, flex: 1 },
          { headerName: "Quantity", field: "quantity", cellRenderer, flex: 1 },
          { headerName: "Unit Price", field: "unit_price", cellRenderer: priceFormatter, flex: 1 },
          { headerName: "Total Cost", field: "total_cost", cellRenderer: priceFormatter, flex: 1 },
        ],
        defaultColDef: {
          flex: 1,
          minWidth: 100,
          resizable: true,
        },
      },
      getDetailRowData: (params) => {
        params.successCallback(params.data.items);
      },
    };
  }, [cellRenderer, priceFormatter]);

  const actionsRenderer = (params) => {
    return (
      <div className="action-table-data edit-delete-action d-flex gap-1">
        <a
            className="p-1 confirm-text"
            title="Delete Order"  
            onClick={() => handleDelete(params.data.id)}
        >
          <Trash2 className="feather-delete" style={{ width: 16, height: 16 }}/>
        </a>
      </div>
    );
  };

  const handleDelete = (orderId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteListOrder(orderId);
          Swal.fire("Deleted!", "The list order has been deleted.", "success");
          fetchOrderData(page, pageSize);
        } catch (error) {
          console.error("Error deleting list order:", error);
          Swal.fire("Error!", "There was an error deleting the list order.", "error");
        }
      }
    });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const exportToExcel = () => {
    if (!orders || orders.length === 0) {
      Swal.fire("No Data", "There is no data to export.", "info");
      return;
    }

    const exportData = [];
    orders.forEach(order => {
        if (order.items && order.items.length > 0) {
            order.items.forEach(item => {
                exportData.push({
                    "#": order.index,
                    "Brand": order.brand_name || "-",
                    "Invoice Number": order.invoice_number || "-",
                    "Invoice Date": order.invoice_date ? new Date(order.invoice_date).toLocaleDateString() : "-",
                    "Supplier": order.supplier_name || "-",
                    "Status": order.status_name || "-",
                    "List": item.list_name || "-",
                    "Segment": item.segment_name || "-",
                    "Quantity": item.quantity || 0,
                    "Unit Price": item.unit_price ? `$${parseFloat(item.unit_price).toFixed(2)}` : "$0.00",
                    "Total Cost": item.total_cost ? `$${parseFloat(item.total_cost).toFixed(2)}` : "$0.00",
                });
            });
        } else {
            exportData.push({
                "#": order.index,
                "Brand": order.brand_name || "-",
                "Invoice Number": order.invoice_number || "-",
                "Invoice Date": order.invoice_date ? new Date(order.invoice_date).toLocaleDateString() : "-",
                "Supplier": order.supplier_name || "-",
                "Status": order.status_name || "-",
                "List": "-",
                "Segment": "-",
                "Quantity": 0,
                "Unit Price": "$0.00",
                "Total Cost": "$0.00",
            });
        }
    });

    secureExportToExcel(exportData, "List_Orders_List");
  };

  const columns = [
    {
      title: "Brand",
      dataIndex: "brand_name",
      render: (text, record) => (record.orderRowSpan > 0 ? <span>{text || "-"}</span> : null),
      onCell: (record) => ({ rowSpan: record.orderRowSpan }),
    },
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      render: (text, record) => (record.orderRowSpan > 0 ? <span>{text || "-"}</span> : null),
      onCell: (record) => ({ rowSpan: record.orderRowSpan }),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      render: (text, record) => (record.orderRowSpan > 0 ? <span>{dateFormatter({ value: text })}</span> : null),
      onCell: (record) => ({ rowSpan: record.orderRowSpan }),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      render: (text, record) => (record.orderRowSpan > 0 ? <span>{text || "-"}</span> : null),
      onCell: (record) => ({ rowSpan: record.orderRowSpan }),
    },
    {
      title: "Status",
      dataIndex: "status_name",
      render: (text, record) => (record.orderRowSpan > 0 ? <span>{text || "-"}</span> : null),
      onCell: (record) => ({ rowSpan: record.orderRowSpan }),
    },
    { title: "List", dataIndex: "list_name", render: (text) => text || "-" },
    { title: "Segment", dataIndex: "segment_name", render: (text) => text || "-" },
    { title: "Quantity", dataIndex: "quantity", render: (text) => text || 0 },
    { title: "Unit Price", dataIndex: "unit_price", render: (text) => priceFormatter({ value: text }) },
    { title: "Total Cost", dataIndex: "total_cost", render: (text) => priceFormatter({ value: text }) },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        record.orderRowSpan > 0 ? actionsRenderer({ data: record }) : null
      ),
      onCell: (record) => ({ rowSpan: record.orderRowSpan }),
    },
  ];

  return (
    <div className="p-1">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>List Orders</h4>
            </div>
          </div>
          <ul className="table-top-head">
            {/* Search, Export, Refresh, etc. can be added here */}
          </ul>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={orders.filter(order => 
                  Object.values(order).some(val => 
                    String(val).toLowerCase().includes(searchQuery.toLowerCase())
                  )
                )}
                loading={loading}
                pagination={{
                  total: totalItems,
                  pageSize: pageSize,
                  current: page,
                  onChange: handlePageChange,
                  showSizeChanger: true,
                  onShowSizeChange: handlePageChange,
                  pageSizeOptions: ['50', '100', '500', '1000', '5000'],
                }}
                bordered
              />
            </div>
          </div>
        </div>
    </div>
  );
};

export default ListOrdersList;
